import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';

// One line: share of Replai's notes rated 👎, as a trailing moving average
// (sum of 👎 / sum of notes over the window, fewer days at the very start).
// Faint dots show each day's own value behind the line.
const COLOR = '#eb6834';
const WINDOW = 7;
// Fixed pixel height; width follows the container so text never scales.
const H = 140;
const PAD = { l: 34, r: 10, t: 10, b: 20 };

const fmtDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const pctOf = (v, t) => (t ? Math.round((1000 * v) / t) / 10 : null);

const Line = ({ days, max }) => {
  const [hover, setHover] = useState(null);
  const [W, setW] = useState(600);
  const wrap = useRef(null);
  useLayoutEffect(() => {
    if (!wrap.current) return undefined;
    setW(Math.max(200, Math.round(wrap.current.getBoundingClientRect().width)));
    const ro = new ResizeObserver(([e]) => setW(Math.max(200, Math.round(e.contentRect.width))));
    ro.observe(wrap.current);
    return () => ro.disconnect();
  }, []);
  const n = days.length;
  const x = (i) => PAD.l + (n > 1 ? i / (n - 1) : 0.5) * (W - PAD.l - PAD.r);
  const y = (v) => PAD.t + (1 - v / max) * (H - PAD.t - PAD.b);
  const line = days.map((d, i) => (d.avg == null ? null : `${x(i)},${y(d.avg)}`)).filter(Boolean);
  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor((W - PAD.l) / 64))));
  const ticks = days.map((d, i) => i).filter((i) => (n - 1 - i) % every === 0);
  const lastIdx = n - 1;
  const onMove = (e) => {
    const px = e.clientX - wrap.current.getBoundingClientRect().left;
    const i = Math.round(((px - PAD.l) / (W - PAD.l - PAD.r)) * (n - 1));
    setHover(i >= 0 && i < n ? i : null);
  };
  const h = hover != null ? days[hover] : null;

  return (
    <div ref={wrap} className="relative min-w-0">
      <svg viewBox={`0 0 ${W} ${H}`} height={H} className="block w-full" role="img"
        aria-label={`Notes rated thumbs down, ${WINDOW}-day moving average`} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        {[0, max / 2, max].map((v) => (
          <g key={v}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke="var(--line-2)" strokeWidth="1" />
            <text x={PAD.l - 6} y={y(v) + 3.5} textAnchor="end" fontSize="11" fill="var(--ink-3)">{v}%</text>
          </g>
        ))}
        {ticks.map((i) => (
          <text key={i} x={x(i)} y={H - 5} textAnchor={i === 0 && n > 1 ? 'start' : i === lastIdx && n > 1 ? 'end' : 'middle'}
            fontSize="11" fill="var(--ink-3)">{fmtDay(days[i].date)}</text>
        ))}
        {days.map((d, i) => d.dayP != null && (
          <circle key={d.date} cx={x(i)} cy={y(Math.min(d.dayP, max))} r="3" fill={COLOR} opacity="0.35" />
        ))}
        {line.length > 1 && <polyline points={line.join(' ')} fill="none" stroke={COLOR} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
        {days[lastIdx]?.avg != null && (
          <circle cx={x(lastIdx)} cy={y(days[lastIdx].avg)} r="4" fill={COLOR} stroke="var(--surface)" strokeWidth="2" />
        )}
        {h && h.avg != null && (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={PAD.t} y2={H - PAD.b} stroke="var(--ink-3)" strokeWidth="1" strokeDasharray="2 3" />
            <circle cx={x(hover)} cy={y(h.avg)} r="4" fill={COLOR} stroke="var(--surface)" strokeWidth="2" />
          </g>
        )}
      </svg>
      {h && h.avg != null && (
        <div className="pointer-events-none absolute top-0 z-10 whitespace-nowrap rounded-md border border-[var(--line)] bg-[var(--surface)] px-2.5 py-1.5 text-xs shadow-sm"
          style={x(hover) > W / 2 ? { right: W - x(hover) + 8 } : { left: x(hover) + 8 }}>
          <div className="font-semibold">{fmtDay(h.date)}</div>
          <div><span className="num font-semibold">{h.avg}%</span> rated 👎, {h.span}-day average</div>
          <div className="text-[var(--ink-3)]">That day: {h.wrong} of {h.total} notes ({h.dayP ?? 0}%) · {h.right} 👍</div>
        </div>
      )}
    </div>
  );
};

const FeedbackTrend = ({ days = 60 }) => {
  const [d, setD] = useState(null);
  useEffect(() => { axiosInstance.get('api/replai/v2/feedback/trend/', { params: { days } }).then(({ data }) => setD(data)).catch(() => setD(false)); }, [days]);
  const rows = useMemo(() => {
    if (!d) return [];
    const all = d.series.map((p) => ({ ...p, total: p.right + p.wrong + p.none }));
    const first = all.findIndex((p) => p.total > 0);
    if (first < 0) return [];
    const live = all.slice(first);
    return live.map((p, i) => {
      const win = live.slice(Math.max(0, i - WINDOW + 1), i + 1);
      const t = win.reduce((a, r) => a + r.total, 0);
      return { ...p, dayP: pctOf(p.wrong, p.total), avg: pctOf(win.reduce((a, r) => a + r.wrong, 0), t), span: win.length };
    });
  }, [d]);
  if (d === false) return null;
  if (d == null) return <div className="h-36 animate-pulse rounded-lg bg-[var(--line-2)]" />;
  const last = rows[rows.length - 1];
  const weekAgo = rows.length > WINDOW ? rows[rows.length - 1 - WINDOW] : null;
  const delta = last && weekAgo && last.avg != null && weekAgo.avg != null ? Math.round((last.avg - weekAgo.avg) * 10) / 10 : null;
  const peak = Math.max(0, ...rows.map((r) => r.avg || 0), ...rows.map((r) => r.dayP || 0));
  const max = Math.max(10, Math.ceil(peak / 5) * 5);
  const sum = (k) => rows.slice(-WINDOW).reduce((a, r) => a + r[k], 0);

  return (
    <figure className="min-w-0">
      <figcaption className="mb-1 flex flex-wrap items-baseline gap-x-2">
        <span className="num text-xl font-semibold">{last?.avg ?? '-'}%</span>
        <span className="text-sm font-medium">of notes rated 👎</span>
        <span className="text-xs text-[var(--ink-3)]">
          {sum('wrong')} of {sum('total')} notes, last {WINDOW} days
          {delta != null && <> · <b className={delta <= 0 ? 'text-green-700' : 'text-red-700'}>{delta > 0 ? '+' : ''}{delta} pts</b> vs a week earlier</>}
          {' '}· lower is better · {sum('right')} rated 👍
        </span>
      </figcaption>
      <Line days={rows} max={max} />
      <p className="mt-1 text-xs text-[var(--ink-3)]">Line: {WINDOW}-day moving average. Dots: each day on its own.</p>
    </figure>
  );
};

export default FeedbackTrend;
